import { ReactNode, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Hub } from 'aws-amplify/utils'
import { analytics } from '@/common/analytics/analytics'
import { USER_ID_TOKEN } from '@/common/constants'
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth'

import { AuthScreen } from '@/auth/AuthPage'
import { APP_ROUTES } from '@/common/constants'
import useUserStore from '@/user/userStore'
import useAccountStore from '@/account/accountStore'
import { FullScreenLoader } from '@/common/ui/FullScreenLoader'
import { useAcceptPolicy } from '@/user/userQueries'
import { clearStoredPasskeyIfUserMismatch } from '@/common/pwa/webauthn'
import {
    SIX_DEVICE_AUTH_VERIFIED,
    SIX_DEVICE_AUTH_LAST_ACTIVITY_TS,
    SIX_PENDING_SIGNUP_TERMS_VERSION_ID,
} from '@/common/storage/keys'
import useAuthStore from './authStore'
import { AuthEvents, AuthTab } from './types'

const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [isLoading, setIsLoading] = useState(true)
    const [isLoggedIn, setIsLoggedIn] = useState(false)
    const { clearAll } = useUserStore()
    const clearAllAccounts = useAccountStore((s) => s.clearAll)
    const { setAuthDetails } = useAuthStore()
    const acceptPolicy = useAcceptPolicy()
    const location = useLocation()

    useEffect(() => {
        if (!isLoggedIn || !acceptPolicy.isIdle) return

        const pendingSignupTermsVersion = sessionStorage.getItem(SIX_PENDING_SIGNUP_TERMS_VERSION_ID)
        if (!pendingSignupTermsVersion) return

        acceptPolicy.mutate(pendingSignupTermsVersion)
    }, [isLoggedIn, acceptPolicy])

    useEffect(() => {
        const userConsentAlreadyCaptured =
            acceptPolicy.isError && acceptPolicy?.error?.response?.data?.message === 'user consent already captured'

        if (!(acceptPolicy.isSuccess || userConsentAlreadyCaptured)) return

        sessionStorage.removeItem(SIX_PENDING_SIGNUP_TERMS_VERSION_ID)
    }, [acceptPolicy.isSuccess, acceptPolicy.isError, acceptPolicy.error])

    const storeTokens = async () => {
        try {
            const tokens = (await fetchAuthSession()).tokens
            const idToken = tokens?.idToken?.toString()
            if (!idToken) {
                return
            }
            const currentUser = await getCurrentUser()
            setAuthDetails(currentUser)
            clearStoredPasskeyIfUserMismatch(currentUser.userId ?? currentUser.username)
            sessionStorage.setItem(USER_ID_TOKEN, idToken)
            setIsLoggedIn(true)
        } catch {
            // no session available
        }
    }

    const clearTokens = async () => {
        sessionStorage.removeItem(USER_ID_TOKEN)
    }

    useEffect(() => {
        ;(async () => {
            await storeTokens()
            setIsLoading(false)
        })()

        const hubListener = Hub.listen('auth', async ({ payload }) => {
            switch (payload.event) {
                case AuthEvents.TOKEN_REFRESH:
                    await storeTokens()
                    break
                case AuthEvents.SIGNED_IN:
                    await storeTokens()

                    sessionStorage.setItem(SIX_DEVICE_AUTH_VERIFIED, '1')
                    sessionStorage.setItem(SIX_DEVICE_AUTH_LAST_ACTIVITY_TS, Date.now().toString())
                    setIsLoggedIn(true)
                    break
                case AuthEvents.SIGN_IN_WITH_REDIRECT:
                    break
                case AuthEvents.SIGNED_OUT:
                    clearTokens()
                    sessionStorage.removeItem(SIX_PENDING_SIGNUP_TERMS_VERSION_ID)

                    setAuthDetails(null)
                    clearAll()
                    clearAllAccounts()
                    setIsLoggedIn(false)
                    analytics.trackWithDefaults('User SignedOut')
                    break
            }
        })

        return () => {
            hubListener()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clearAll])

    if (isLoading) return <FullScreenLoader dark />

    if (isLoggedIn) {
        return <>{children}</>
    }

    // allow the router to render the signup/login pages so route props (eg. tab) are respected
    if (location.pathname === APP_ROUTES.SIGNUP) {
        return <AuthScreen tab={AuthTab.SIGNUP} />
    }

    return <AuthScreen />
}

export default AuthProvider
